import Parse from "parse/dist/parse.min.js";
import store from "@/lib/store/store";
import { startOfDay, startOfNextDay } from "./exportHelpers";

/**
 * Data access for the diary bulk export.
 *
 * The Vuex store only holds the entry links inside the currently
 * selected month window (the subscription in
 * store/plugins/diary/entries.js refetches on every month change), so
 * a custom export range must run its own query. The query shape
 * mirrors the subscription query: resolve all Client rows sharing the
 * focused client's uuid, then OR of public entries and private
 * entries authored by the current user.
 */

/**
 * True when the entry link's entry carries at least one of the
 * selected attributes (OR semantics). An empty selection means "no
 * attribute filter".
 *
 * filter: { ids: [ String ], names: [ String ] } - both derived from
 * the selected Attribute objects. Matching prefers the Attribute id;
 * the name comparison is a fallback for legacy per-entry Attribute
 * copies that do not share ids with the catalog.
 */
export function entryMatchesAttributeFilter(entryLink, filter) {
	if (!filter || !filter.ids || filter.ids.length === 0) { return true; }
	const entry = entryLink.attributes.entry;
	const attributes = entry ? entry.attributes.attributes : undefined;
	if (!attributes || attributes.length === 0) { return false; }
	return attributes.some((attribute) => {
		if (filter.ids.indexOf(attribute.id) > -1) { return true; }
		return filter.names.indexOf(attribute.attributes.name) > -1;
	});
}

/**
 * Fetch the entry links for the export.
 *
 * options:
 *   clientId        : id of the focused client (required)
 *   from            : Date lower bound (inclusive, start of day) or
 *                     undefined for no lower bound
 *   to              : Date upper bound (inclusive, whole day) or
 *                     undefined for no upper bound
 *   attributeFilter : { ids, names } see entryMatchesAttributeFilter
 *   sortOrder       : "asc" (oldest first, default) or "desc"
 *
 * Returns EntryLink[] with entry, author dependency, client, group,
 * attributes and attribute configs included.
 */
export async function fetchEntryLinksForExport(options) {
	const { clientId, from, to, attributeFilter, sortOrder } = options;

	// A client can exist as several rows (one per group); entries hang
	// off all of them, connected through a shared uuid.
	const clientQuery = new Parse.Query("Client");
	clientQuery.equalTo("objectId", clientId);
	clientQuery.notEqualTo("deleted", true);
	const clients = await clientQuery.find();
	if (clients.length === 0) { return []; }

	const allClientsQuery = new Parse.Query("Client");
	allClientsQuery.equalTo("uuid", clients[0].attributes.uuid);
	allClientsQuery.notEqualTo("deleted", true);
	const allClients = await allClientsQuery.find();

	const userPointer = {
		__type: "Pointer",
		className: "_User",
		objectId: store.getters["users/getUserId"],
	};

	const publicQuery = new Parse.Query("EntryLink");
	publicQuery.notEqualTo("deleted", true);
	publicQuery.notEqualTo("isPrivate", true);
	publicQuery.containedIn("client", allClients);

	const privateQuery = new Parse.Query("EntryLink");
	privateQuery.notEqualTo("deleted", true);
	privateQuery.equalTo("isPrivate", true);
	privateQuery.equalTo("author", userPointer);
	privateQuery.containedIn("client", allClients);

	for (const query of [ publicQuery, privateQuery ]) {
		if (from) {
			query.greaterThanOrEqualTo("createdAt", startOfDay(from));
		}
		if (to) { query.lessThan("createdAt", startOfNextDay(to)); }
	}

	const mainQuery = Parse.Query.or(publicQuery, privateQuery);
	if (sortOrder === "desc") { mainQuery.descending("createdAt"); }
	else { mainQuery.ascending("createdAt"); }
	mainQuery.include([
		"entry",
		"entry.author.dependency",
		"client",
		"client.group",
		"entry.attributes",
		"entry.attributes.attribute_config",
		"entry.otherFiles",
	]);
	// Parse defaults to 100 rows; an "Alle" export over years must not
	// silently truncate.
	mainQuery.limit(100000);

	const results = await mainQuery.find();
	return results.filter((entryLink) =>
		entryMatchesAttributeFilter(entryLink, attributeFilter));
}
